import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, coachInbox, coachThread, coachReply, coachVoice, correctMeal, runTrackersNow, setThreadMemberManager, clientBody, clientHealth, correctMeasurement } from "@/lib/api";
import { atLeast } from "@/lib/roles";
import { useStaffSocket } from "@/lib/realtime";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { MessageSquare, Send, Pencil, Plus, Trash2, AlertCircle, ClipboardList, Mic } from "lucide-react";

// Chats update live over the socket; polling is the safety net (fast only while it's down).
const POLL_INBOX_MS = 10000;
const POLL_THREAD_MS = 5000;
const SLOW_POLL_MS = 60000;
const MEAL_TYPES = ["breakfast", "lunch", "snack", "dinner"];
const MEAL_STATUS = {
  estimating: { label: "Estimating…", cls: "text-zinc-400 bg-zinc-500/10" },
  needs_coach: { label: "Needs your numbers", cls: "text-amber-300 bg-amber-500/10" },
  not_food: { label: "Not food", cls: "text-zinc-400 bg-zinc-500/10" },
  deleted: { label: "Removed by client", cls: "text-zinc-500 bg-zinc-500/10" },
  corrected: { label: "Corrected", cls: "text-emerald-300 bg-emerald-500/10" },
  estimated: { label: "Bot estimate", cls: "text-sky-300 bg-sky-500/10" },
};

const mediaUrl = (id) => `${api.defaults.baseURL}/app/media/${id}`;
const cap = (s) => (s ? s[0].toUpperCase() + s.slice(1) : "");
const fmtTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date().toDateString() === d.toDateString();
  return today ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    : d.toLocaleDateString([], { day: "numeric", month: "short" }) + " " + d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

export default function MemberAppInbox() {
  const { activeFacility, effectiveRole, user } = useAuth();
  const isManager = atLeast(effectiveRole || user?.role, "manager");
  const [threads, setThreads] = useState(null);
  const [posting, setPosting] = useState(false);
  const [mine, setMine] = useState(false);
  const [filter, setFilter] = useState("");
  const [dueOnly, setDueOnly] = useState(false);
  // ?thread=<id> opens that chat (links from Accountability's "Needs attention").
  const [params] = useSearchParams();
  const [selected, setSelected] = useState(() => params.get("thread"));
  const [repliesDue, setRepliesDue] = useState(0);

  const loadInbox = useCallback(async () => {
    try {
      const r = await coachInbox(mine);
      setThreads(r.threads || []);
      setRepliesDue(r.replies_due || 0);
    } catch {
      setThreads((t) => t || []);
    }
  }, [mine]);

  const [live, setLive] = useState({});        // thread_id -> event count, so the open chat reloads
  const [typing, setTyping] = useState({});    // thread_id -> member name, for a few seconds
  const refresh = useRef(null);
  const onEvent = useCallback((e) => {
    if (e.type === "message" || e.type === "update") {
      setLive((l) => ({ ...l, [e.thread_id]: (l[e.thread_id] || 0) + 1 }));
      if (e.type === "message" && e.message?.sender_type === "client") setTyping((t) => ({ ...t, [e.thread_id]: null }));
      clearTimeout(refresh.current);
      refresh.current = setTimeout(loadInbox, 300);
    } else if (e.type === "typing" && e.who === "client") {
      setTyping((t) => ({ ...t, [e.thread_id]: e.name || "Member" }));
      setTimeout(() => setTyping((t) => ({ ...t, [e.thread_id]: null })), 5000);
    }
  }, [loadInbox]);
  const { connected, sendTyping } = useStaffSocket(activeFacility?.id, onEvent);

  useEffect(() => {
    loadInbox();
    const id = setInterval(loadInbox, connected ? SLOW_POLL_MS : POLL_INBOX_MS);
    return () => clearInterval(id);
  }, [loadInbox, activeFacility?.id, connected]);
  const firstFacility = useRef(activeFacility?.id);
  useEffect(() => {
    if (firstFacility.current !== activeFacility?.id) setSelected(null);
    firstFacility.current = activeFacility?.id;
  }, [activeFacility?.id]);

  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return (threads || [])
      .filter((t) => !dueOnly || ["due", "late"].includes(t.reply?.state))
      .filter((t) => !q || `${t.client?.name || ""} ${t.coach_name || ""} ${t.member_manager_name || ""}`.toLowerCase().includes(q));
  }, [threads, filter, dueOnly]);
  const unread = (threads || []).reduce((n, t) => n + (t.unread || 0), 0);

  const postTrackers = async () => {
    setPosting(true);
    try {
      const r = await runTrackersNow();
      toast.success(r.posted ? `Posted ${r.posted} daily tracker${r.posted === 1 ? "" : "s"}` : "Every active chat already has today's tracker");
      loadInbox();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not post trackers");
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="coach-inbox-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow">Member App · Coaching</div>
          <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">Coach Inbox</h2>
          <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
            Chats with challenge clients at {activeFacility?.name || "this gym"}. Coach AInish estimates the meals they log;
            check the numbers and correct any that look off.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-sm text-zinc-400">
            {repliesDue ? <span className="mr-3 font-semibold text-amber-300" data-testid="inbox-replies-due">{repliesDue} repl{repliesDue === 1 ? "y" : "ies"} due by 10 am</span> : null}
            {unread ? <span className="text-zinc-100 font-semibold">{unread} unread</span> : "All caught up"}
          </div>
          {isManager && (
            <Button variant="outline" onClick={postTrackers} disabled={posting} data-testid="post-trackers"
              title="Coach AInish posts each client's daily tracker at 9 pm. This posts today's now, for chats that don't have one yet.">
              <ClipboardList className="mr-1.5 h-4 w-4" /> {posting ? "Posting…" : "Post today's trackers now"}
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[340px_1fr]">
        <div className="card-surface flex flex-col overflow-hidden lg:h-[calc(100vh-240px)]">
          <div className="space-y-2 border-b border-[#1f1f1f] p-3">
            <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Find a client…" data-testid="inbox-filter" />
            <label className="flex items-center gap-2 text-xs text-zinc-400">
              <input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} data-testid="inbox-mine" />
              Only my clients
            </label>
            <label className="flex items-center gap-2 text-xs text-zinc-400">
              <input type="checkbox" checked={dueOnly} onChange={(e) => setDueOnly(e.target.checked)} data-testid="inbox-due-only" />
              Only replies due (yesterday&apos;s day, before 10 am)
            </label>
          </div>
          <div className="flex-1 overflow-y-auto" data-testid="inbox-threads">
            {threads === null ? (
              <div className="p-6 text-center text-sm text-zinc-500">Loading…</div>
            ) : shown.length === 0 ? (
              <div className="p-6 text-center text-sm text-zinc-500">
                <MessageSquare className="mx-auto mb-2 h-5 w-5 text-zinc-600" />
                No chats yet. They start when a 6 Week Challenge client is added on App Clients.
              </div>
            ) : shown.map((t) => (
              <button key={t.id} type="button" onClick={() => setSelected(t.id)} data-testid={`inbox-thread-${t.id}`}
                className={`block w-full border-b border-[#161616] px-4 py-3 text-left transition hover:bg-white/[0.03] ${selected === t.id ? "bg-white/[0.05]" : ""}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className={`truncate text-sm ${t.unread ? "font-bold text-zinc-50" : "font-medium text-zinc-200"}`}>{t.client?.name || "Client"}</span>
                  <span className="shrink-0 text-[10px] text-zinc-500">{fmtTime(t.last_message_at)}</span>
                </div>
                <div className="mt-0.5 flex items-center justify-between gap-2">
                  <span className="truncate text-xs text-zinc-500">{t.last_message_preview || "No messages yet"}</span>
                  <span className="flex shrink-0 items-center gap-1">
                    <ReplyBadge reply={t.reply} />
                    {t.unread > 0 && <span className="rounded-full bg-[#E5262B] px-1.5 text-[10px] font-bold text-white">{t.unread}</span>}
                  </span>
                </div>
                <div className="mt-1 flex items-center gap-2 text-[10px] text-zinc-500">
                  <span>Coach {t.coach_name || "—"}</span>
                  {t.member_manager_name && <span>· MM {t.member_manager_name}</span>}
                  <span>·</span>
                  <span>{t.today?.kcal ? `${t.today.kcal.toLocaleString()}${t.today.kcal_target ? ` / ${t.today.kcal_target.toLocaleString()}` : ""} kcal today` : "No meals today"}</span>
                  {t.today?.needs_coach > 0 && <span className="text-amber-300">· {t.today.needs_coach} to log</span>}
                  {t.status === "ended" && <span>· ended</span>}
                </div>
              </button>
            ))}
          </div>
        </div>

        {selected ? <ThreadPane key={selected} threadId={selected} onChange={loadInbox} isManager={isManager}
          liveTick={live[selected] || 0} typingName={typing[selected]} connected={connected} onTyping={sendTyping} /> : (
          <div className="card-surface flex items-center justify-center p-10 text-sm text-zinc-500 lg:h-[calc(100vh-240px)]">
            Pick a chat on the left.
          </div>
        )}
      </div>
    </div>
  );
}

function ThreadPane({ threadId, onChange, isManager, liveTick, typingName, connected, onTyping }) {
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [editing, setEditing] = useState(null);
  const [measuring, setMeasuring] = useState(null);
  const bottom = useRef(null);
  const lastId = useRef(null);

  const load = useCallback(async () => {
    try {
      setData(await coachThread(threadId));
    } catch {
      toast.error("Could not load this chat");
    }
  }, [threadId]);

  useEffect(() => {
    load().then(onChange);
  }, [load, onChange]);
  useEffect(() => {
    const id = setInterval(load, connected ? SLOW_POLL_MS : POLL_THREAD_MS);
    return () => clearInterval(id);
  }, [load, connected]);
  useEffect(() => { if (liveTick) load(); }, [liveTick, load]);   // something happened in this chat

  // Scroll to the newest message when one arrives.
  useEffect(() => {
    const last = data?.messages?.[data.messages.length - 1]?.id;
    if (last && last !== lastId.current) {
      lastId.current = last;
      bottom.current?.scrollIntoView({ block: "end" });
    }
  }, [data]);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await coachReply(threadId, text.trim());
      setText("");
      await load();
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Message not sent");
    } finally {
      setSending(false);
    }
  };

  if (!data) return <div className="card-surface p-8 text-center text-sm text-zinc-500">Loading…</div>;
  const { thread, client, messages, day, last_review: review } = data;
  const targets = day?.targets;

  return (
    <div className="card-surface flex flex-col overflow-hidden lg:h-[calc(100vh-240px)]" data-testid="inbox-thread-pane">
      <div className="border-b border-[#1f1f1f] px-5 py-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <div className="font-semibold text-zinc-100">
              {client?.name || "Client"}
              {typingName && <span className="ml-2 text-xs font-normal text-emerald-300">typing…</span>}
              <span className={`ml-2 inline-block h-1.5 w-1.5 rounded-full align-middle ${connected ? "bg-emerald-400" : "bg-zinc-600"}`}
                title={connected ? "Live: new messages appear instantly" : "Reconnecting… (checking every few seconds)"} />
            </div>
            <div className="text-xs text-zinc-500">
              {thread.programme || "Accountability"}{thread.end_date ? ` · ends ${thread.end_date.slice(0, 10)}` : ""} · Coach {thread.coach_name || "—"}
              {thread.status === "ended" && " · programme ended (chat stays open; meal logging is off)"}
            </div>
            <MemberManagerLine thread={thread} canEdit={isManager} onSaved={() => { load(); onChange(); }} />
            <BodyActivityLine clientId={thread.client_id} refreshKey={messages.length} />
            {review && <LastReview review={review} />}
          </div>
          <div className="flex gap-4 text-xs text-zinc-400" data-testid="inbox-day-totals">
            <DayStat label="kcal" value={day.totals.kcal} target={targets?.kcal} />
            <DayStat label="protein g" value={day.totals.protein_g} target={targets?.protein_g} />
            <DayStat label="meals" value={day.meals.length} />
          </div>
        </div>
      </div>

      <div className="flex-1 space-y-2 overflow-y-auto px-5 py-4" data-testid="inbox-messages">
        {messages.length === 0 && <div className="py-10 text-center text-sm text-zinc-500">No messages yet. Say hello!</div>}
        {messages.map((m) => <Bubble key={m.id} m={m} onEdit={setEditing} onMeasure={setMeasuring} />)}
        <div ref={bottom} />
      </div>

      <div className="flex items-end gap-2 border-t border-[#1f1f1f] p-3">
        <textarea value={text} onChange={(e) => { setText(e.target.value); if (e.target.value) onTyping(threadId); }} rows={2} maxLength={4000}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={`Reply to ${client?.name?.split(" ")[0] || "client"}… (Enter to send, Shift+Enter for a new line)`}
          className="min-h-[44px] flex-1 resize-none rounded-md border border-[#262626] bg-[#0b0b0b] px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-zinc-600"
          data-testid="inbox-reply" />
        {text.trim() ? (
          <Button onClick={send} disabled={sending} data-testid="inbox-send"><Send className="h-4 w-4" /></Button>
        ) : (
          <VoiceButton threadId={threadId} onSent={() => { load(); onChange(); }} />
        )}
      </div>

      {measuring && <MeasurementDialog id={measuring} clientId={thread.client_id} onClose={() => setMeasuring(null)}
        onSaved={() => { setMeasuring(null); load(); }} />}
      {editing && <MealDialog meal={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); onChange(); }} />}
    </div>
  );
}

/** Yesterday's day still needs a reply (QC spec: before 10:00 gym time), or it was answered. */
function ReplyBadge({ reply }) {
  if (!reply) return null;
  if (reply.state === "replied") return <span className="text-[10px] text-emerald-400" title="Yesterday's day is answered">✓ replied</span>;
  const late = reply.state === "late";
  return (
    <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${late ? "bg-rose-500/15 text-rose-300" : "bg-amber-500/15 text-amber-300"}`}
      title={late ? "Past 10 am: reply now, it still counts as a reply" : "Reply to yesterday's day before 10 am"} data-testid="reply-badge">
      {late ? "Reply late" : `Reply by ${new Date(reply.due_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`}
    </span>
  );
}

/** How the last scored reply in this chat did on the QC (out of 10), and what it missed. */
function LastReview({ review }) {
  const tone = review.band === "Green" ? "text-emerald-300" : review.band === "Amber" ? "text-amber-300" : "text-rose-300";
  const when = new Date(`${review.date}T00:00:00`).toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" });
  return (
    <div className="mt-1 text-xs text-zinc-500" data-testid="last-review">
      Your reply to {when}:{" "}
      {review.status === "no_reply" ? <span className="text-rose-300">no reply</span>
        : review.total == null ? <span>not scored yet</span>
          : <span className={tone}>{review.total}/10 {review.band}</span>}
      {review.missed?.length && review.status !== "no_reply" ? <span> · missed: {review.missed.join(", ").toLowerCase()}</span> : null}
    </div>
  );
}

function DayStat({ label, value, target }) {
  return (
    <div className="text-right">
      <div className="font-display text-lg font-bold text-zinc-100">
        {Number(value || 0).toLocaleString()}{target ? <span className="text-xs font-normal text-zinc-500"> / {Number(target).toLocaleString()}</span> : null}
      </div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label} today</div>
    </div>
  );
}

// Older staff messages have no role: they came from the coach.
function staffLabel(m) {
  if (m.sender_role === "member_manager") return `${m.sender_name || ""} · Member manager`;
  if (m.sender_role === "staff") return m.sender_name || "Staff";
  return `Coach ${m.sender_name || ""}`;
}

function MemberManagerLine({ thread, canEdit, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(thread.member_manager_name || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await setThreadMemberManager(thread.id, name.trim());
      toast.success(name.trim() ? `${name.trim()} is now in this chat` : "Member manager removed from this chat");
      setEditing(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };
  if (editing) {
    return (
      <div className="mt-1 flex items-center gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Member manager's name" className="h-7 w-48 text-xs" autoFocus
          onKeyDown={(e) => e.key === "Enter" && save()} />
        <Button size="sm" className="h-7" onClick={save} disabled={saving}>Save</Button>
        <Button size="sm" variant="ghost" className="h-7" onClick={() => setEditing(false)}>Cancel</Button>
      </div>
    );
  }
  return (
    <div className="text-xs text-zinc-500">
      Member manager {thread.member_manager_name || "—"}
      {canEdit && (
        <button type="button" className="ml-2 text-zinc-400 underline-offset-2 hover:underline" onClick={() => setEditing(true)}
          data-testid="inbox-member-manager-edit">
          {thread.member_manager_name ? "Change" : "Add"}
        </button>
      )}
    </div>
  );
}

const SITES = ["neck", "chest", "waist", "hips", "arm", "thigh", "calf"];

// Latest weight / waist / body fat with change since the start, and the last 7 days of steps and sleep.
function BodyActivityLine({ clientId, refreshKey }) {
  const [body, setBody] = useState(null);
  const [act, setAct] = useState(null);
  useEffect(() => {
    clientBody(clientId).then(setBody).catch(() => setBody(null));
    clientHealth(clientId).then(setAct).catch(() => setAct(null));
  }, [clientId, refreshKey]);
  const ch = (f, u) => (body?.changes?.[f] != null ? ` (${body.changes[f] > 0 ? "+" : ""}${body.changes[f]} ${u})` : "");
  const l = (f) => body?.latest?.[f]?.value;
  return (
    <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-zinc-500" data-testid="inbox-body-activity">
      {body && (l("weight_kg") != null || l("waist_cm") != null) ? (
        <span>
          {l("weight_kg") != null && `${l("weight_kg")} kg${ch("weight_kg", "kg")}`}
          {l("waist_cm") != null && ` · waist ${l("waist_cm")} cm${ch("waist_cm", "cm")}`}
          {l("body_fat_pct") != null && ` · BF ≈ ${l("body_fat_pct")}%`}
          {body.overdue && <span className="text-amber-300"> · check due</span>}
        </span>
      ) : <span>No measurement checks yet</span>}
      {act?.week?.avg_steps != null ? (
        <span>
          {act.week.avg_steps.toLocaleString()} steps/day{act.week.avg_sleep_h != null ? ` · ${act.week.avg_sleep_h} h sleep` : ""} (7 days
          {act.connection?.source ? `, ${act.connection.source}` : ""})
          {act.rows.some((r) => r.flags?.length) && <span className="text-amber-300"> · odd data flagged</span>}
        </span>
      ) : <span>No watch data</span>}
    </div>
  );
}

function MeasurementDialog({ id, clientId, onClose, onSaved }) {
  const [row, setRow] = useState(null);
  const [form, setForm] = useState({});
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    clientBody(clientId).then((b) => {
      const r = b.rows.find((x) => x.id === id);
      setRow(r || null);
      if (r) setForm(Object.fromEntries(["weight_kg", ...SITES.map((s) => `${s}_cm`)].map((f) => [f, r[f] ?? ""])));
    }).catch(() => toast.error("Couldn't load the check"));
  }, [id, clientId]);
  const save = async () => {
    const body = Object.fromEntries(Object.entries(form).filter(([f, v]) => v !== "" && Number(v) !== row[f]).map(([f, v]) => [f, Number(v)]));
    setSaving(true);
    try {
      await correctMeasurement(id, { ...body, note: note.trim() || null });
      toast.success("Check corrected; the member's card is updated");
      onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(Array.isArray(d) ? "One of those values is out of range" : d || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Measurement check {row ? `· ${row.date}` : ""}</DialogTitle>
          <DialogDescription>Correct a value the member entered. The card in their chat is updated and marked as checked by you.</DialogDescription>
        </DialogHeader>
        {!row ? <div className="text-sm text-zinc-500">Loading…</div> : (
          <div className="space-y-3">
            {row.flags?.length > 0 && (
              <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-2 text-xs text-amber-200">
                Flagged: {row.flags.map((f) => `${f.field.replace("_cm", "").replace("_kg", "")} ${f.change > 0 ? "+" : ""}${f.change} since ${f.previous_date}`).join(", ")}
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              {Object.keys(form).map((f) => (
                <label key={f} className="text-xs text-zinc-400">
                  {f === "weight_kg" ? "Weight (kg)" : `${f.replace("_cm", "")} (cm)`}
                  <Input type="number" step="0.1" value={form[f]} onChange={(e) => setForm({ ...form, [f]: e.target.value })} className="mt-1 h-8" />
                </label>
              ))}
            </div>
            <div className="text-xs text-zinc-500">
              Total {row.total_cm ?? "—"} cm · body fat ≈ {row.body_fat_pct ?? "—"}% (recalculated on save)
            </div>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional), e.g. re-measured at the gym" />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={onClose}>Cancel</Button>
              <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save correction"}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const clock = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
const MAX_VOICE_SECONDS = 180;

// The browser's own recorder. MP4/AAC where supported (Chrome 126+, Safari) so phones of every
// kind can play it; WebM/Opus otherwise.
function pickMime() {
  if (typeof MediaRecorder === "undefined") return null;
  return ["audio/mp4;codecs=mp4a.40.2", "audio/mp4", "audio/webm;codecs=opus", "audio/webm"]
    .find((t) => MediaRecorder.isTypeSupported(t)) || "";
}

function VoiceButton({ threadId, onSent }) {
  const [state, setState] = useState("idle");      // idle | recording | sending
  const [secs, setSecs] = useState(0);
  const rec = useRef(null);
  const chunks = useRef([]);
  const started = useRef(0);
  const timer = useRef(null);
  const keep = useRef(true);

  const cleanup = () => {
    clearInterval(timer.current);
    rec.current?.stream?.getTracks().forEach((t) => t.stop());
  };
  useEffect(() => () => { keep.current = false; if (rec.current?.state === "recording") rec.current.stop(); cleanup(); }, []);

  const start = async () => {
    const mime = pickMime();
    if (mime === null) { toast.error("This browser can't record audio"); return; }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      toast.error("Allow the microphone for this site to record a voice note");
      return;
    }
    const r = new MediaRecorder(stream, mime ? { mimeType: mime, audioBitsPerSecond: 64000 } : undefined);
    chunks.current = [];
    keep.current = true;
    r.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
    r.onstop = async () => {
      cleanup();
      const duration = Math.min(MAX_VOICE_SECONDS, (Date.now() - started.current) / 1000);
      if (!keep.current) { setState("idle"); return; }
      if (duration < 1) { toast.error("That was under a second"); setState("idle"); return; }
      setState("sending");
      try {
        const blob = new Blob(chunks.current, { type: r.mimeType || mime || "audio/webm" });
        const base64 = await new Promise((resolve, reject) => {
          const fr = new FileReader();
          fr.onload = () => resolve(String(fr.result).split(",", 2)[1]);
          fr.onerror = () => reject(fr.error);
          fr.readAsDataURL(blob);
        });
        await coachVoice(threadId, { base64, mime: blob.type, duration_s: Math.round(duration * 10) / 10 });
        onSent();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Voice note not sent");
      } finally {
        setState("idle");
      }
    };
    rec.current = Object.assign(r, { stream });
    started.current = Date.now();
    setSecs(0);
    r.start();
    setState("recording");
    timer.current = setInterval(() => {
      const s = (Date.now() - started.current) / 1000;
      setSecs(s);
      if (s >= MAX_VOICE_SECONDS) r.stop();
    }, 250);
  };
  const stop = (send) => { keep.current = send; rec.current?.stop(); };

  if (state === "recording") {
    return (
      <div className="flex items-center gap-2" data-testid="inbox-voice-recording">
        <span className="flex items-center gap-1.5 text-xs text-zinc-300 tabular-nums">
          <span className="h-2 w-2 animate-pulse rounded-full bg-[#E5262B]" /> {clock(secs)}
        </span>
        <Button variant="ghost" size="icon" title="Discard" onClick={() => stop(false)}><Trash2 className="h-4 w-4" /></Button>
        <Button onClick={() => stop(true)} title="Send voice note" data-testid="inbox-voice-send"><Send className="h-4 w-4" /></Button>
      </div>
    );
  }
  return (
    <Button onClick={start} disabled={state === "sending"} title="Record a voice note" data-testid="inbox-voice">
      {state === "sending" ? "…" : <Mic className="h-4 w-4" />}
    </Button>
  );
}

function Bubble({ m, onEdit, onMeasure }) {
  const client = m.sender_type === "client";
  const bot = m.sender_type === "bot";
  const meal = m.meal;
  // The client's meal message carries the edit button when the bot didn't reply (bot off, failed, or needs a coach).
  const showMeal = meal && (m.kind === "meal_card" || (m.kind === "meal" && !["estimated", "corrected"].includes(meal.status)));
  return (
    <div className={`flex ${client ? "justify-start" : "justify-end"}`}>
      <div className={`${m.kind === "tracker" ? "w-72 max-w-[95%]" : "max-w-[75%]"} rounded-xl px-3 py-2 text-sm ${client ? "bg-[#151515] border border-[#232323]" : bot ? "bg-[#101418] border border-sky-500/20" : "bg-[#E5262B]/90 text-white"}`}>
        <div className={`mb-0.5 text-[11px] font-semibold ${client ? "text-zinc-400" : bot ? "text-sky-300" : "text-white/80"}`}>
          {bot && <img src="/ainish.png" alt="" className="mr-1.5 inline h-5 w-5 rounded-full bg-sky-500/10 align-middle" />}
          {client ? m.sender_name : bot ? "Coach AInish" : staffLabel(m)}
          <span className="ml-2 font-normal opacity-70">{fmtTime(m.created_at)}</span>
        </div>
        {m.kind === "meal" && (
          <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-zinc-400">{cap(meal?.meal_type || "meal")}</div>
        )}
        {m.kind === "voice" && m.media_id ? (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <audio controls preload="metadata" src={mediaUrl(m.media_id)} className="h-9 w-64" />
              <span className="text-[11px] opacity-70">{clock(m.duration_s || 0)}</span>
            </div>
            {m.transcript && <div className="max-w-xs text-[13px] italic opacity-80">“{m.transcript}”</div>}
            {m.meal_log_id && client && <div className="text-[11px] text-sky-300">Logged as a meal by Coach AInish</div>}
          </div>
        ) : m.media_id && (
          <a href={mediaUrl(m.media_id)} target="_blank" rel="noreferrer">
            <img src={mediaUrl(m.media_id)} alt="Meal" className="mb-1 h-48 w-48 rounded-lg object-cover" />
          </a>
        )}
        {m.kind === "meal_card" && meal ? <MealSummary meal={meal} body={m.body} /> : m.kind === "tracker" && m.tracker?.meals ? (
          <TrackerSummary tr={m.tracker} body={m.body} />
        ) : m.body ? (
          <div className="whitespace-pre-wrap text-zinc-100">{m.body}</div>
        ) : null}
        {m.kind === "feedback" && (
          <div className={`mt-1 text-[11px] ${m.answered ? "text-emerald-300" : "text-zinc-500"}`}>
            Week {m.week} check-in · {m.answered ? "answered (managers see the answers on Coach Scores)" : "not answered yet"}
          </div>
        )}
        {m.kind === "measurement" && m.measurement_id && (
          <div className="mt-2 flex justify-end">
            <button type="button" onClick={() => onMeasure(m.measurement_id)} className="flex items-center gap-1 text-xs text-zinc-300 hover:text-white"
              data-testid={`edit-measurement-${m.measurement_id}`}>
              <Pencil className="h-3 w-3" /> Review / correct
            </button>
          </div>
        )}
        {showMeal && (
          <div className="mt-2 flex items-center justify-between gap-3">
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${(MEAL_STATUS[meal.status] || {}).cls}`}>{(MEAL_STATUS[meal.status] || {}).label}</span>
            {meal.status !== "deleted" && meal.status !== "estimating" && (
              <button type="button" onClick={() => onEdit(meal)} className="flex items-center gap-1 text-xs text-zinc-300 hover:text-white"
                data-testid={`edit-meal-${meal.id}`}>
                <Pencil className="h-3 w-3" /> {meal.status === "needs_coach" || meal.status === "not_food" ? "Add numbers" : "Correct"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function MealSummary({ meal, body }) {
  const t = meal.totals || {};
  // The reply's running total and question date from the bot's estimate; drop them once corrected.
  const rest = meal.status === "corrected" ? [] : (body || "").split("\n").slice(1);
  return (
    <div className="space-y-1.5">
      <div className="text-zinc-100"><b>{cap(meal.meal_type)}</b> · {Number(t.kcal || 0).toLocaleString()} kcal
        <span className="text-zinc-400"> · P {t.protein_g} g · C {t.carbs_g} g · F {t.fat_g} g · Fibre {t.fibre_g} g</span></div>
      <ul className="space-y-0.5 text-xs text-zinc-400">
        {(meal.items || []).map((i, n) => (
          <li key={n} className="flex justify-between gap-4"><span>{i.quantity} {i.unit} {i.name}</span><span>{i.kcal} kcal</span></li>
        ))}
      </ul>
      {meal.confidence === "low" && meal.status === "estimated" && (
        <div className="flex items-center gap-1 text-xs text-amber-300"><AlertCircle className="h-3 w-3" /> Low confidence: worth a check</div>
      )}
      {meal.status === "corrected" && <div className="text-xs text-emerald-300">Corrected by {meal.corrected_by_name || "coach"}</div>}
      {rest.map((l) => <div key={l} className="text-xs text-zinc-500">{l}</div>)}
    </div>
  );
}

const TRACKER_ROWS = [["kcal", "Calories", "kcal"], ["protein_g", "Protein", "g"], ["carbs_g", "Carbs", "g"], ["fat_g", "Fat", "g"], ["fibre_g", "Fibre", "g"]];

function TrackerSummary({ tr, body }) {
  const lines = (body || "").split("\n");
  return (
    <div className="space-y-1.5">
      <div className="font-semibold text-zinc-100">{lines[0]}</div>
      {TRACKER_ROWS.map(([k, label, unit]) => {
        const v = tr.totals?.[k] || 0;
        const t = tr.targets?.[k];
        // Protein and fibre are goals to reach; calories, carbs and fat are budgets not to overshoot.
        const goal = k === "protein_g" || k === "fibre_g";
        const good = t && (goal ? v >= t * 0.9 : v <= t * (k === "kcal" ? 1.05 : 1.1));
        return (
          <div key={k}>
            <div className="flex justify-between text-xs text-zinc-400">
              <span>{label}</span><span className="whitespace-nowrap">{v.toLocaleString()}{t ? ` / ${t.toLocaleString()}` : ""} {unit}</span>
            </div>
            {t ? (
              <div className="mt-0.5 h-1.5 overflow-hidden rounded bg-[#0b0b0b]">
                <div className={`h-1.5 rounded ${good ? "bg-emerald-400" : goal ? "bg-[#E5262B]" : "bg-amber-400"}`} style={{ width: `${Math.min(100, (100 * v) / t)}%` }} />
              </div>
            ) : null}
          </div>
        );
      })}
      {lines.slice(3).map((l) => <div key={l} className="text-xs text-zinc-500">{l}</div>)}
    </div>
  );
}

const ITEM_FIELDS = [
  { key: "kcal", label: "kcal" }, { key: "protein_g", label: "P g" }, { key: "carbs_g", label: "C g" },
  { key: "fat_g", label: "F g" }, { key: "fibre_g", label: "Fibre g" },
];
const blankItem = () => ({ name: "", quantity: 1, unit: "", kcal: "", protein_g: "", carbs_g: "", fat_g: "", fibre_g: "" });

function MealDialog({ meal, onClose, onSaved }) {
  const [mealType, setMealType] = useState(meal.meal_type);
  const [items, setItems] = useState(meal.items?.length ? meal.items.map((i) => ({ ...i })) : [blankItem()]);
  const [saving, setSaving] = useState(false);

  const set = (n, k, v) => setItems((list) => list.map((it, i) => (i === n ? { ...it, [k]: v } : it)));
  const total = (k) => items.reduce((s, i) => s + (Number(i[k]) || 0), 0);

  const save = async () => {
    const clean = items.filter((i) => i.name.trim());
    if (!clean.length) { toast.error("Add at least one item"); return; }
    setSaving(true);
    try {
      await correctMeal(meal.id, {
        meal_type: mealType,
        items: clean.map((i) => ({
          name: i.name.trim(), quantity: Number(i.quantity) || 0, unit: i.unit || "",
          ...Object.fromEntries(ITEM_FIELDS.map((f) => [f.key, Number(i[f.key]) || 0])),
        })),
      });
      toast.success("Meal updated. The client sees the new numbers.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl bg-[#0f0f0f] border-[#262626] text-zinc-100" data-testid="meal-dialog">
        <DialogHeader>
          <DialogTitle>{meal.status === "needs_coach" ? "Add the numbers" : "Correct this meal"}</DialogTitle>
          <DialogDescription className="text-zinc-400">
            {meal.text ? <>Client&apos;s note: “{meal.text}”. </> : null}
            Totals recalculate from the items. The client&apos;s chat card and daily total update straight away.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-start gap-4">
            {meal.media_id && <img src={mediaUrl(meal.media_id)} alt="Meal" className="h-32 w-32 shrink-0 rounded-lg object-cover" />}
            <div className="flex flex-wrap gap-2">
              {MEAL_TYPES.map((t) => (
                <button key={t} type="button" onClick={() => setMealType(t)}
                  className={`rounded-full border px-3 py-1 text-xs ${mealType === t ? "border-[#E5262B] bg-[#E5262B]/15 text-zinc-100" : "border-[#262626] text-zinc-400"}`}>{cap(t)}</button>
              ))}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500">
                  <th className="py-1 pr-2">Item</th><th className="py-1 pr-2">Qty</th><th className="py-1 pr-2">Unit</th>
                  {ITEM_FIELDS.map((f) => <th key={f.key} className="py-1 pr-2">{f.label}</th>)}<th />
                </tr>
              </thead>
              <tbody>
                {items.map((it, n) => (
                  <tr key={n}>
                    <td className="py-1 pr-2"><Input value={it.name} onChange={(e) => set(n, "name", e.target.value)} className="h-8 min-w-[140px] bg-[#0b0b0b] border-[#262626] text-zinc-100" placeholder="e.g. Roti" /></td>
                    <td className="py-1 pr-2"><Input type="number" value={it.quantity} onChange={(e) => set(n, "quantity", e.target.value)} className="h-8 w-16 bg-[#0b0b0b] border-[#262626] text-zinc-100" /></td>
                    <td className="py-1 pr-2"><Input value={it.unit} onChange={(e) => set(n, "unit", e.target.value)} className="h-8 w-20 bg-[#0b0b0b] border-[#262626] text-zinc-100" placeholder="piece" /></td>
                    {ITEM_FIELDS.map((f) => (
                      <td key={f.key} className="py-1 pr-2"><Input type="number" min="0" value={it[f.key]} onChange={(e) => set(n, f.key, e.target.value)} className="h-8 w-[72px] bg-[#0b0b0b] border-[#262626] text-zinc-100" data-testid={`meal-item-${n}-${f.key}`} /></td>
                    ))}
                    <td className="py-1"><button type="button" onClick={() => setItems((l) => l.filter((_, i) => i !== n))} className="text-zinc-500 hover:text-rose-300" aria-label="Remove item"><Trash2 className="h-4 w-4" /></button></td>
                  </tr>
                ))}
                <tr className="text-zinc-300">
                  <td className="pt-2 font-semibold" colSpan={3}>Total</td>
                  {ITEM_FIELDS.map((f) => <td key={f.key} className="pt-2 font-semibold">{Math.round(total(f.key))}</td>)}<td />
                </tr>
              </tbody>
            </table>
          </div>
          <Button variant="outline" size="sm" onClick={() => setItems((l) => [...l, blankItem()])}><Plus className="mr-1 h-3.5 w-3.5" /> Add item</Button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="meal-save">{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
